import * as React from "react";
import type {IValueDidChange, Lambda} from "mobx";
import styled from "styled-components";
import {observer} from "mobx-react";
import {observe} from "mobx";
import {flushSync} from "react-dom";
import {withAppStores} from "../../../../AppStores";
import type {Space} from "../../../../data/models/Space";
import type {Xyicon} from "../../../../data/models/Xyicon";
import type {IFilterState} from "../../../../data/models/filter/Filter";
import type {InvisibleLinkedXyiconType} from "../../../modules/space/spaceeditor/ui/actionbar/LinkedXyiconsWindow";
import type {PortTemplateDto, SpaceFileInsertionInfo, MarkupType} from "../../../../generated/api/base";
import type {DockableTitle} from "../../../modules/space/spaceeditor/ui/toolbar/Dockable";
import type {ISpaceItemModel} from "../../../../data/models/Model";
import type {AppState} from "../../../../data/state/AppState";
import type {Catalog} from "../../../../data/models/Catalog";
import type {SelectionToolType, SpaceTool} from "../../../modules/space/spaceeditor/logic3d/features/tools/Tools";
import type {ThemeType} from "../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import type {SpaceItem} from "../../../modules/space/spaceeditor/logic3d/elements3d/SpaceItem";
import type {IinheritedMethods} from "../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import type {IBasicPropsFor3DTweaking} from "../../../modules/space/SpaceView";
import {ClipboardManager} from "../../../modules/space/spaceeditor/logic3d/managers/ClipboardManager";
import {SpaceEditorMode} from "../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import {SpaceToolBarType} from "../../../modules/space/spaceeditor/logic3d/features/tools/Tools";
import {Constants} from "../../../modules/space/spaceeditor/logic3d/Constants";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {FocusLoss} from "../../../../utils/ui/focus/FocusLoss";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {Permission, XyiconFeature} from "../../../../generated/api/base";
import {NotificationType} from "../../../notification/Notification";
import {notify} from "../../../../utils/Notify";
import {WarningWindow} from "../../../modules/abstract/popups/WarningWindow";
import {Functions} from "../../../../utils/function/Functions";
import {Signal} from "../../../../utils/signal/Signal";
import {XyiconSizeChanger} from "../../../modules/space/spaceeditor/ui/sceneoptions/XyiconSizeChanger";
import {SpaceContextMenu} from "../../../modules/space/spaceeditor/ui/actionbar/SpaceContextMenu";
import {SpaceAlignViewBarV5} from "../space/SpaceAlignViewBarV5";
import {colorPalette} from "../../styles/colorPalette";
import {radius} from "../../styles/styles";
import {NavigationEnum} from "../../../../Enums";
import {PopupWindowV5} from "../../popup/PopupWindowV5";
import type {IViewTab} from "../../topbar/ViewTabsCommon";
import {EventNameForViewsChangeInLocalStorage, getLocalStorageKeyForWorkspaceViews} from "../../topbar/ViewTabsCommon";
import {EmptyListViewV5} from "../../details/EmptyListViewV5";
import {CreateSpaceUtils} from "../space/CreateSpaceUtils";
import {getClipboardData} from "../../../modules/space/spaceeditor/logic3d/managers/ClipboardUtils";
import {AlertV5} from "../../popup/AlertV5";
import type {ViewChangeType} from "../../../../data/models/ViewUtils";
import {doesSingleSelectedItemShowEditButton} from "../../../modules/space/spaceeditor/logic3d/managers/spaceitems/SelectionHandleUtils";
import {areActionBarSemanticStatesEqual} from "../../../modules/space/spaceeditor/ui/FloatingOverlayUtils";
import {SPLITTER_V5_CATALOG_MAIN_SECOND_PANE_MIN_WIDTH_PX} from "../../layout/splitterLayoutMath";
import {SpaceToolBarV5} from "./toolbar/SpaceToolBarV5";
import {SpaceControlBarV5} from "./spacecontrolbar/SpaceControlBarV5";
import {SpaceSelector} from "./spaceselector/SpaceSelector";
import {SpaceLoadingScreenV5} from "./SpaceLoadingScreenV5";
import {SpaceViewBarV5} from "./viewbar/SpaceViewBarV5";
import {DockableV5} from "./dockable/DockableV5";
import {SpaceActionBarV5} from "./spaceactionbar/SpaceActionBarV5";
import type {ISpaceActionBarV5Ref} from "./spaceactionbar/SpaceActionBarV5";
import {LinkedXyiconsWindowV5} from "./links/LinkedXyiconsWindowV5";
import {PortSelectorV5} from "./links/PortSelectorV5";
import {MergeBoundariesWindowV5} from "./MergeBoundariesWindowV5";
import {MeasureToolBarV5} from "./spacecontrolbar/MeasureToolBarV5";
import type {TSpaceEditorPanelTypes} from "./SpaceEditorPanelV5";
import {LinkLineActionBarV5} from "./links/LinkLineActionBarV5";
import {LockIconTooltip} from "./LockIconTooltip";

interface ISpaceEditorProps extends IBasicPropsFor3DTweaking {
	readonly appState?: AppState;
	readonly mode: SpaceEditorMode;
	readonly thumbnail?: string;
	readonly isCatalogOpen?: boolean;
	readonly isCatalogDocked?: boolean;
	readonly isBoundaryTypesWindowOpen?: boolean;
	readonly isBoundaryTypesWindowDocked?: boolean;
	readonly isUnplottedXyiconsOpen?: boolean;
	readonly isUnplottedXyiconsDocked?: boolean;
	readonly activeToolId: SpaceTool;
	readonly selectionToolType: SelectionToolType;
	readonly activePanel?: TSpaceEditorPanelTypes;
	readonly isDetailsPanelOpen?: boolean;
	readonly isSideGridOpen?: boolean;
	readonly setDockableDocked?: (value: boolean, title: DockableTitle) => void;
	readonly setDockableOpen?: (value: boolean, title?: DockableTitle) => void;
	readonly setScale: (spaceUnitsPerMeter: number, applyToAllSpaces?: boolean) => void;
	readonly confirmAlignment: (insertionInfo: SpaceFileInsertionInfo) => void;
	readonly setActiveTool: (id: SpaceTool) => void;
	readonly selectItems?: (spaceItems: ISpaceItemModel[], selectDetailsContent?: boolean, forceUpdate?: boolean, doubleClick?: boolean) => void;
	readonly focusItems?: (spaceItems: ISpaceItemModel[]) => void;
	readonly getFocusedItems: () => ISpaceItemModel[];
	readonly onAddCatalogClick: (event: React.MouseEvent) => void;
	readonly onDuplicateCatalogClick: (catalog: Catalog) => void;
	readonly toggleDetailsPanel: () => void;
	readonly onOpenPanelClick: (type: TSpaceEditorPanelTypes) => void;
	readonly onCloseActivePanel?: () => void;
	readonly forceUpdateDetailsPanel?: () => void;
	readonly closeSideGridPopups?: () => void;
}

interface ISpaceEditorState {
	actionBar: {
		isOpen: boolean;
		isLinkable: boolean /** If only xyicons and are selected */;
		isEditable: boolean /** If only one boundary, or one markup is selected */;
		isInEditMode: boolean;
	};
	portSelector: {
		isOpen: boolean;
		ports: PortTemplateDto[];
		item: Xyicon;
		type: "from" | "to";
	};
	contextMenu: {
		isOpen: boolean;
	};
	linkBreakers: {
		fromObjectIds: string[];
	};
	linkedXyicons: {
		fromObjectIds: string[];
		type: InvisibleLinkedXyiconType;
	};
	mergeBoundariesWindow: {
		isOpen: boolean;
	};
	activeToolBar: SpaceToolBarType;
	dropdownStyle: boolean;
	saveViewDropdownStyle: boolean;
	isNoActiveView: boolean;
}

type IActionBarState = ISpaceEditorState["actionBar"];
type IPortSelectorState = ISpaceEditorState["portSelector"];
type IContextMenuState = ISpaceEditorState["contextMenu"];

const createActionBarState = (overrides: Partial<IActionBarState> = {}): IActionBarState => ({
	isOpen: false,
	isLinkable: false,
	isEditable: false,
	isInEditMode: false,
	...overrides,
});

const createPortSelectorState = (overrides: Partial<IPortSelectorState> = {}): IPortSelectorState => ({
	isOpen: false,
	ports: [],
	item: null,
	type: "from",
	...overrides,
});

const createContextMenuState = (isOpen: boolean = false): IContextMenuState => ({
	isOpen,
});

@withAppStores("appState")
@observer
export class SpaceEditorV5 extends React.Component<ISpaceEditorProps, ISpaceEditorState> {
	private _themeObserverDisposer: Lambda;
	private _canvasContainer = React.createRef<HTMLDivElement>();
	private _spaceToolBarRef = React.createRef<SpaceToolBarV5>();
	private _spaceActionBarRef = React.createRef<ISpaceActionBarV5Ref>();
	private _portSelectorRef = React.createRef<HTMLDivElement>();
	private _spaceSelectorRef = React.createRef<HTMLDivElement>();
	private _spaceViewBarRef = React.createRef<HTMLDivElement>();
	private _saveViewBarRef = React.createRef<HTMLDivElement>();
	private _actionBarWorldPosition = {x: 0, y: 0};
	private _actionBarSelectionKey: string = "";
	private _portSelectorWorldPosition = {x: 0, y: 0};
	private _contextMenuWorldPosition = {x: 0, y: 0};
	private readonly _floatingOverlaySignals = {
		portSelectorUpdated: Signal.create(),
		contextMenuUpdated: Signal.create(),
	};
	private _defaultSpaceViewBarWidth = 0;
	private marginLeftRight = 32;
	private saveViewBarWidthInExpandedMode = 352;

	private readonly resizeObserver = new ResizeObserver(() => {
		if (this.spaceViewRenderer.isMounted) {
			this.spaceViewRenderer.resizeCanvasImmediately();
		}

		const dropdownStyle = this.getDropdownStyle();
		const saveViewDropdownStyle = this.getSaveDropdownStyle();

		if (dropdownStyle !== this.state.dropdownStyle) {
			this.setState({dropdownStyle});
		}

		if (saveViewDropdownStyle !== this.state.saveViewDropdownStyle) {
			this.setState({saveViewDropdownStyle});
		}
	});

	constructor(props: ISpaceEditorProps) {
		super(props);
		this.state = {
			actionBar: createActionBarState(),
			portSelector: createPortSelectorState(),
			contextMenu: createContextMenuState(),
			linkBreakers: {
				fromObjectIds: [],
			},
			linkedXyicons: {
				fromObjectIds: [],
				type: null,
			},
			mergeBoundariesWindow: {
				isOpen: false,
			},
			activeToolBar: SpaceToolBarType.PrimaryToolBar,
			dropdownStyle: false,
			saveViewDropdownStyle: false,
			isNoActiveView: false,
		};
	}

	private readonly getActionBarWorldPosition = () => this._actionBarWorldPosition;

	private readonly getPortSelectorWorldPosition = () => this._portSelectorWorldPosition;

	private readonly getContextMenuWorldPosition = () => this._contextMenuWorldPosition;

	private readonly closeSaveViewRelatedPopups = () => {
		this.props.closeSideGridPopups?.();
		this.props.onCloseActivePanel?.();
	};

	private updateFloatingOverlayState(hasSemanticChange: boolean, applySemanticState: () => void, positionUpdateSignal: {dispatch: () => void}) {
		if (hasSemanticChange) {
			applySemanticState();
			return;
		}

		positionUpdateSignal.dispatch();
	}

	private createOpenActionBarState(): IActionBarState {
		const {spaceItemController} = this.props.spaceViewRenderer;
		const selectedItems = spaceItemController.selectedItems;
		const selectedItem = selectedItems[0];
		const selectedMarkupType =
			selectedItem?.spaceItemType === "markup"
				? (((selectedItem as {type?: MarkupType | null} | undefined)?.type as MarkupType | null | undefined) ?? null)
				: null;
		const selectedItemPermission =
			selectedItem?.modelData != null
				? this.props.spaceViewRenderer.actions.getModuleTypePermission(selectedItem.modelData.typeId, selectedItem.modelData.ownFeature)
				: null;
		const isEditable =
			selectedItems.length === 1 &&
			doesSingleSelectedItemShowEditButton(
				this.props.spaceViewRenderer.isV5,
				selectedItem?.spaceItemType,
				selectedMarkupType,
				selectedItemPermission,
			);

		return createActionBarState({
			isOpen: true,
			isLinkable: selectedItems.every((spaceItem: SpaceItem) => spaceItem.spaceItemType === "xyicon"),
			isEditable,
			isInEditMode: isEditable && this.state.actionBar.isInEditMode,
		});
	}

	private getActionBarSelectionKey(): string {
		return this.props.spaceViewRenderer.spaceItemController.selectedItems.map((item: SpaceItem) => item.id).join(",");
	}

	public handleLocalStorageViewChange = () => {
		const {user, organizationIdByAuthData} = this.props.appState;
		const localStorageKeyForWorkspaceViews = getLocalStorageKeyForWorkspaceViews(user?.id, organizationIdByAuthData);
		const updatedValue = localStorage.getItem(localStorageKeyForWorkspaceViews);
		const viewIdsInLocalStorage: IViewTab[] = updatedValue ? JSON.parse(updatedValue) : [];

		this.setState({isNoActiveView: viewIdsInLocalStorage.length === 0});
	};

	private onAddClick = () => {
		CreateSpaceUtils.triggerSpaceCreatePopup();
	};

	private openPortSelector = (worldX: number, worldY: number, item: Xyicon, ports: PortTemplateDto[], type: "from" | "to") => {
		this._portSelectorWorldPosition = {x: worldX, y: worldY};

		const newPortSelectorState = createPortSelectorState({
			isOpen: true,
			item,
			ports,
			type,
		});
		const currentPortSelectorState = this.state.portSelector;
		const hasSemanticChange =
			!currentPortSelectorState.isOpen ||
			currentPortSelectorState.item !== item ||
			currentPortSelectorState.type !== type ||
			currentPortSelectorState.ports !== ports;

		this.updateFloatingOverlayState(
			hasSemanticChange,
			() =>
				this.setState({
					portSelector: newPortSelectorState,
				}),
			this._floatingOverlaySignals.portSelectorUpdated,
		);
	};

	private closePortSelector = () => {
		if (this.state.portSelector.isOpen) {
			this._portSelectorWorldPosition = {x: 0, y: 0};
			this.setState({
				portSelector: createPortSelectorState(),
			});
		}
	};

	private openContextMenu = (worldX: number, worldY: number) => {
		if (this.spaceViewRenderer.mode === SpaceEditorMode.NORMAL) {
			const {currentPointerWorld} = this.spaceViewRenderer.toolManager.activeTool;

			currentPointerWorld.x = worldX;
			currentPointerWorld.y = worldY;
			this._contextMenuWorldPosition = {x: worldX, y: worldY};

			this.updateFloatingOverlayState(
				!this.state.contextMenu.isOpen,
				() =>
					this.setState({
						contextMenu: createContextMenuState(true),
					}),
				this._floatingOverlaySignals.contextMenuUpdated,
			);
			this.closeActionBar();
		}
	};

	private isContextMenuOpen = () => {
		return this.state.contextMenu.isOpen;
	};

	private closeContextMenu = () => {
		if (this.state.contextMenu.isOpen) {
			this._contextMenuWorldPosition = {x: 0, y: 0};
			this.setState({
				contextMenu: createContextMenuState(),
			});
		}
	};

	private onDeleteClick = () => {
		const {spaceItemController} = this.spaceViewRenderer;
		const selectedItems = spaceItemController.selectedItems;
		const {actions} = this.props.appState;

		const xyiconsWithoutPermissionCount = actions.getNumberOfSpaceItemsWithoutPermission(selectedItems, Permission.Delete);

		if (xyiconsWithoutPermissionCount === 0) {
			this.closeContextMenu();
			spaceItemController.onDeleteClick();
		} else {
			notify(this.props.appState.app.notificationContainer, {
				type: NotificationType.Warning,
				title: "Action not allowed!",
				description: `${xyiconsWithoutPermissionCount} of ${selectedItems.length} objects you have selected do not have the required permissions to perform this action. To obtain permission, contact your organization's administrator.`,
				lifeTime: Infinity,
			});
		}
	};

	private openLinkBreakers = (fromObjectIds: string[]) => {
		this.setState({
			linkBreakers: {
				fromObjectIds: fromObjectIds,
			},
		});
	};

	private updateLinkBreakers = () => {
		if (this.state.linkBreakers.fromObjectIds.length > 0) {
			const fromObjectIds = this.state.linkBreakers.fromObjectIds;

			this.setState({
				linkBreakers: {
					fromObjectIds: fromObjectIds,
				},
			});
		}
	};

	private closeLinkBreakers = () => {
		if (this.state.linkBreakers.fromObjectIds.length > 0) {
			this.setState({
				linkBreakers: {
					fromObjectIds: [],
				},
			});
		}
	};

	private openLinkedXyiconsWindow = (fromObjectIds: string[], type: InvisibleLinkedXyiconType) => {
		this.setState({
			linkedXyicons: {
				fromObjectIds: fromObjectIds,
				type: type,
			},
		});
	};

	private closeLinkedXyiconsWindow = () => {
		if (this.state.linkedXyicons.fromObjectIds.length > 0) {
			this.setState({
				linkedXyicons: {
					fromObjectIds: [],
					type: null,
				},
			});
		}
	};

	private openMergeBoundariesWindow = () => {
		if (!this.state.mergeBoundariesWindow.isOpen) {
			this.setState({
				mergeBoundariesWindow: {
					isOpen: true,
				},
			});
		}
	};

	private closeMergeBoundariesWindow = () => {
		if (this.state.mergeBoundariesWindow.isOpen) {
			this.setState({
				mergeBoundariesWindow: {
					isOpen: false,
				},
			});
		}
	};

	private openActionBar = (worldX: number, worldY: number): boolean => {
		let hasChanged: boolean = false;
		this._actionBarWorldPosition = {x: worldX, y: worldY};

		const newActionbarState = this.createOpenActionBarState();
		const selectionKey = this.getActionBarSelectionKey();
		const hasSelectionChanged = this._actionBarSelectionKey !== selectionKey;

		if (!areActionBarSemanticStatesEqual(this.state.actionBar, newActionbarState) || hasSelectionChanged) {
			this._actionBarSelectionKey = selectionKey;
			flushSync(() => {
				this.setState({
					actionBar: newActionbarState,
				});
			});
			hasChanged = true;
		}

		this.closeContextMenu();

		return hasChanged;
	};

	private closeActionBar = (): boolean => {
		let hasChanged: boolean = false;

		if (this.state.actionBar.isOpen) {
			this._actionBarWorldPosition = {x: 0, y: 0};
			this._actionBarSelectionKey = "";
			flushSync(() => {
				this.setState({
					actionBar: createActionBarState(),
				});
			});

			hasChanged = true;
		}

		return hasChanged;
	};

	private setActiveToolBar = (toolBar: SpaceToolBarType) => {
		if (this.state.activeToolBar !== toolBar) {
			this.setState({
				activeToolBar: toolBar,
			});
		}
	};

	private onEditModeSwitched = (enabled: boolean) => {
		if (this.state.actionBar.isInEditMode !== enabled) {
			this.setState({
				actionBar: {
					...this.state.actionBar,
					isInEditMode: enabled,
				},
			});
		}

		if (enabled) {
			this.props.setActiveTool("selection");
		}
	};

	private onSwitchToTextEditMode = () => {
		this._spaceActionBarRef.current?.onAddTextClick();
		this.props.setActiveTool("selection");
	};

	private onExitTextEditMode = () => {
		this._spaceActionBarRef.current?.onTextEditorClose();
	};

	private onRedrawClick = () => {
		this.spaceViewRenderer.spaceItemController.onRedrawClick();
	};

	private onCancelEditClick = () => {
		this.spaceViewRenderer.spaceItemController.onCancelEditClick();

		requestAnimationFrame(() => {
			if (this.state.actionBar.isInEditMode) {
				this.setState({
					actionBar: {
						...this.state.actionBar,
						isInEditMode: false,
					},
				});
			}
		});
	};

	private updateTheme = () => {
		this.spaceViewRenderer.updateTheme();
	};

	private get inheritedMethods(): IinheritedMethods {
		return {
			openActionBar: this.openActionBar,
			closeActionBar: this.closeActionBar,
			openPortSelector: this.openPortSelector,
			closePortSelector: this.closePortSelector,
			openContextMenu: this.openContextMenu,
			isContextMenuOpen: this.isContextMenuOpen,
			closeContextMenu: this.closeContextMenu,
			selectItems: this.props.selectItems ?? Functions.emptyFunction,
			focusItems: this.props.focusItems ?? Functions.emptyFunction,
			getFocusedItems: this.props.getFocusedItems,
			setScale: this.props.setScale,
			confirmAlignment: this.props.confirmAlignment,
			setActiveTool: this.props.setActiveTool,
			onPasteClick: this.onPasteClick,
			openLinkBreakers: this.openLinkBreakers,
			updateLinkBreakers: this.updateLinkBreakers,
			closeLinkBreakers: this.closeLinkBreakers,
			openLinkedXyiconsWindow: this.openLinkedXyiconsWindow,
			closeLinkedXyiconsWindow: this.closeLinkedXyiconsWindow,
			onEditModeSwitched: this.onEditModeSwitched,
			onSwitchToTextEditMode: this.onSwitchToTextEditMode,
			onExitTextEditMode: this.onExitTextEditMode,
			toggleDetailsPanel: this.props.toggleDetailsPanel,
		};
	}

	private initSpaceViewRenderer() {
		if (this._canvasContainer.current) {
			this._canvasContainer.current.appendChild(this.spaceViewRenderer.canvas);
			this.spaceViewRenderer.mount();
		}
	}

	private getNonDockedTitle(): DockableTitle {
		if (this.props.isCatalogOpen && !this.props.isCatalogDocked) {
			return "Catalog";
		} else if (this.props.isBoundaryTypesWindowOpen && !this.props.isBoundaryTypesWindowDocked) {
			return "Boundary";
		} else if (this.props.isUnplottedXyiconsOpen && !this.props.isUnplottedXyiconsDocked) {
			return "Unplotted Xyicons";
		} else {
			return null;
		}
	}

	private pasteFromClipboard = (event: ClipboardEvent) => {
		if (KeyboardListener.isEventTargetAnInput(event)) {
			// we don't override the default behaviour, let the users paste text into input, and textarea elements
		} else {
			this.paste(event.clipboardData.getData("text/plain"));
		}
	};

	private onPasteClick = async () => {
		if (navigator.clipboard.readText) {
			const {plainText} = await getClipboardData();

			this.paste(plainText);
		} else {
			await WarningWindow.open(
				`It looks like you're missing permissions to read the clipboard. Please try to allow the browser to access the clipboard.<br />
				Alternatively, try to use the <i>Ctrl+C</i> (for copy), and <i>Ctrl+V</i> (for paste) methods.
				${
					Constants.isFirefox
						? `<br /><br />If you're using Firefox, open up a new tab, type <i>about:config</i> as the url you'd like to visit,
				then change the following 3 settings from <i>false</i> to <i>true</i> :<br /><br />
				<div class="hbox justifyCenter"><img src="src/assets/images/spaceviewer/mozilla_firefox_asyncclipboard.png" alt="Firefox clipboard settings" /><div>`
						: ""
				}`,
			);
		}
	};

	private async paste(pastedString: string = "") {
		if (this.props.mode === SpaceEditorMode.NORMAL) {
			if (!ClipboardManager.isPasting && !this.spaceViewRenderer.spaceItemController.isAddingItemsToServer) {
				try {
					const pastedText = StringUtils.formatTableString(pastedString);
					const actionType = ClipboardManager.action;

					await ClipboardManager.paste(pastedText, this.spaceViewRenderer, actionType);

					if (actionType === "CUT") {
						ClipboardManager.clearClipboard();
					}
				} catch (error) {
					console.warn(`Paste error: ${error}`);
					ClipboardManager.resetPasteState();
				}
			} else {
				console.warn("You tried to paste items, while some other items were already being created.");
			}
		}
	}

	private onApplyEditClick = () => {
		if (this.spaceViewRenderer.spaceItemController.currentlyEditedItem?.isValid) {
			this.props.spaceViewRenderer.spaceItemController.switchEditMode(false);
		}
	};

	private keyDown = (event: KeyboardEvent) => {
		if (!ClipboardManager.getSelectedText()) {
			const spaceViewRenderer = this.spaceViewRenderer;

			switch (event.key) {
				case KeyboardListener.KEY_ESCAPE:
					if (!KeyboardListener.isEventTargetAnInput(event) && PopupWindowV5.numberOfOpenPopups < 1 && !spaceViewRenderer.isMeasureToolBarOpen) {
						this.props.setActiveTool("selection");
					}
					break;
				case "c":
				case "C":
				case "d":
				case "D":
					if (KeyboardListener.isCtrlDown) {
						const isDuplicate = event.key.toLowerCase() === "d";

						if (isDuplicate) {
							event.preventDefault(); // ctrl+d => save to bookmark by default
						}
						spaceViewRenderer.copySelectedItemsToClipboard();
						if (isDuplicate) {
							this.onPasteClick();
						}
					}
					break;
				case "x":
				case "X":
					if (KeyboardListener.isCtrlDown) {
						const xyicons = spaceViewRenderer.xyiconManager.selectedItems;
						const boundaries = spaceViewRenderer.boundaryManager.selectedItems;
						const markups = spaceViewRenderer.markupManager.selectedItems;

						if ([...xyicons, ...boundaries, ...markups].every((i) => !i.isPositionLockedAnyWay)) {
							spaceViewRenderer.cutSelectedItemsToClipboard();
						}
					}
					break;
				case "s":
				case "S":
					if (KeyboardListener.isCtrlDown) {
						event.preventDefault(); // by default, the browser tries to save the html file
						spaceViewRenderer.copySelectedItemsToStamp();
					}
					break;
				case "l":
				case "L":
				case "Ò":
					if (KeyboardListener.isShiftDown && KeyboardListener.isAltDown) {
						spaceViewRenderer.unlockSpaceItemPosition();
					}
					if (KeyboardListener.isShiftDown && !KeyboardListener.isAltDown) {
						spaceViewRenderer.lockSpaceItemPosition();
					}
					break;
				case KeyboardListener.KEY_CTRL:
					this.forceUpdate();
					break;
			}
		}
	};

	private keyUp = (event: KeyboardEvent) => {
		if (PopupWindowV5.numberOfOpenPopups < 1) {
			const spaceViewRenderer = this.spaceViewRenderer;

			switch (event.key) {
				case KeyboardListener.KEY_DELETE:
					if (this.state.actionBar.isInEditMode) {
						spaceViewRenderer.boundaryManager.deleteSelectedVertex();
					} else if (spaceViewRenderer.spaceItemController.selectedItems.length > 0) {
						this.onDeleteClick();
					}
					break;
				case KeyboardListener.KEY_ESCAPE:
					if (this.state.actionBar.isInEditMode) {
						this.onCancelEditClick();
					}
					if (this.state.linkedXyicons.fromObjectIds.length) {
						this.closeLinkedXyiconsWindow();
					}
					if (this.state.portSelector.isOpen) {
						this.closePortSelector();
					}
					break;
				case KeyboardListener.KEY_ENTER:
					if (this.state.actionBar.isInEditMode) {
						this.onApplyEditClick();
					}
					break;
				case KeyboardListener.KEY_CTRL:
					this.forceUpdate();
					break;
			}
		}
	};

	private onRealignSpaceClick = () => {
		this._spaceToolBarRef.current?.primaryToolBarRef.current?.openSpaceAlignToolBar();
	};

	public get spaceViewRenderer() {
		return this.props.appState.app.spaceViewRenderer;
	}

	private loadSpace(space: Space) {
		return this.props.spaceViewRenderer.populateData(space);
	}

	private updateSpace = () => {
		const space = this.props.spaceViewRenderer.transport.appState.space;

		if (space) {
			if (this.props.spaceViewRenderer.space?.id !== space.id) {
				return this.loadSpace(space);
			}
		}
	};

	private getDropdownStyle() {
		let bool = false;

		if (this._spaceSelectorRef.current && this._spaceViewBarRef) {
			const {right: box1Right} = this._spaceSelectorRef.current.getBoundingClientRect();
			const {right: box2Right} = this._spaceViewBarRef.current.getBoundingClientRect();

			if (box2Right - box1Right - 10 < this._defaultSpaceViewBarWidth) {
				bool = true;
			}
		}

		return bool;
	}

	private getSaveDropdownStyle() {
		if (this._spaceSelectorRef.current && this._spaceViewBarRef.current) {
			const {right: spaceSelectorRight} = this._spaceSelectorRef.current.getBoundingClientRect();
			const {right: spaceViewBarRight, width: spaceViewBarWidth} = this._spaceViewBarRef.current.getBoundingClientRect();

			return spaceViewBarRight - spaceSelectorRight < this.saveViewBarWidthInExpandedMode + spaceViewBarWidth + this.marginLeftRight;
		}

		return false;
	}

	private getChangedTypes(selectedSpaceEditorView: any): ViewChangeType[] {
		const changeTypes: ViewChangeType[] = ["filters", "captions", "layers", "spotlight", "columns", "column sorts"];
		let changedTypes: ViewChangeType[] = [];

		const oldData = selectedSpaceEditorView.getSavedDataForType("filters", false) as IFilterState;
		const newData = selectedSpaceEditorView.getNewDataForType("filters", false) as IFilterState;

		if (oldData && newData) {
			const normalize = (data: IFilterState) => (data?.type === "advanced" ? (data?.filters?.advanced ?? []) : (data?.filters?.simple ?? []));

			if (JSON.stringify(normalize(oldData)) !== JSON.stringify(normalize(newData))) {
				changedTypes.push("filters");
			}
		}

		const rawChanges = changeTypes.filter((type) => type !== "filters" && selectedSpaceEditorView.hasUnsavedChanges(type, false));

		if (rawChanges.includes("columns") || rawChanges.includes("column sorts")) {
			changedTypes.push("columns");
		}

		changedTypes.push(...rawChanges.filter((t) => t !== "columns" && t !== "column sorts"));

		return changedTypes;
	}

	public override componentDidMount() {
		KeyboardListener.getInstance().signals.down.add(this.keyDown);
		KeyboardListener.getInstance().signals.up.add(this.keyUp);
		window.addEventListener("hashchange", this.updateSpace);

		this.props.appState.app.graphicalTools.pdfRenderer.setProcessOrder("LIFO");
		this.initSpaceViewRenderer();
		this.updateSpace();

		const appState = this.props.appState;

		this._themeObserverDisposer = observe(appState, "theme", (changeValue: IValueDidChange<ThemeType>) => {
			this.updateTheme();
		});

		document.addEventListener("paste", this.pasteFromClipboard);
		FocusLoss.listen(document.body, ClipboardManager.onBlur);
		this.spaceViewRenderer.transport.signals.switchOrganization.add(ClipboardManager.onBlur);

		FocusLoss.listen(this._portSelectorRef.current, this.closePortSelector);
		this.resizeObserver.observe(this._canvasContainer.current);

		if (this._spaceViewBarRef.current) {
			this._defaultSpaceViewBarWidth = this._spaceViewBarRef.current.getBoundingClientRect().width;
		}

		window.addEventListener(EventNameForViewsChangeInLocalStorage, this.handleLocalStorageViewChange);
	}

	public override componentDidUpdate(): void {
		this.updateSpace();
	}

	public override componentWillUnmount() {
		KeyboardListener.getInstance().signals.down.remove(this.keyDown);
		KeyboardListener.getInstance().signals.up.remove(this.keyUp);
		window.removeEventListener("hashchange", this.updateSpace);
		window.removeEventListener(EventNameForViewsChangeInLocalStorage, this.handleLocalStorageViewChange);

		document.removeEventListener("paste", this.pasteFromClipboard);
		this.spaceViewRenderer.unmount();
		FocusLoss.stopListen(document.body, ClipboardManager.onBlur);
		this.spaceViewRenderer.transport.signals.switchOrganization.remove(ClipboardManager.onBlur);
		ClipboardManager.onBlur();

		this._themeObserverDisposer?.();
		this._themeObserverDisposer = null;

		FocusLoss.stopListen(this._portSelectorRef.current, this.closePortSelector);
		this.resizeObserver.disconnect();
	}

	public override render() {
		const spaceViewRenderer = this.spaceViewRenderer;
		const {actionBar, portSelector, contextMenu, linkBreakers, linkedXyicons, mergeBoundariesWindow, isNoActiveView} = this.state;

		this.spaceViewRenderer.setMode(this.props.mode);
		this.spaceViewRenderer.addInheritedMethods(this.inheritedMethods);

		const nonDockedTitle = this.getNonDockedTitle();

		const canvasContainerBorderStyle: React.CSSProperties =
			this.props.mode === SpaceEditorMode.NORMAL
				? {}
				: {
						border: `2px solid ${colorPalette.gray.c300}`,
						borderRadius: radius.sm,
					};

		const {photoSphereSceneManager} = this.props.appState.app.graphicalTools;
		const isInMixedOrSplitViewMode = ["mixed", "split", "transitioning"].includes(photoSphereSceneManager.viewMode);

		const selectedSpaceEditorView = spaceViewRenderer.actions.getSelectedView(XyiconFeature.SpaceEditor);
		const changedTypes = this.getChangedTypes(selectedSpaceEditorView);
		const hasChanges = changedTypes.length > 0;

		const hasDockedToolOpen =
			(this.props.isCatalogOpen && this.props.isCatalogDocked) ||
			(this.props.isBoundaryTypesWindowOpen && this.props.isBoundaryTypesWindowDocked) ||
			(this.props.isUnplottedXyiconsOpen && this.props.isUnplottedXyiconsDocked);
		const splitterMainSecondPaneMinWidthPx =
			this.props.isSideGridOpen === false && hasDockedToolOpen ? SPLITTER_V5_CATALOG_MAIN_SECOND_PANE_MIN_WIDTH_PX : 392;

		return (
			<SpaceEditorStyled
				className="SpaceEditor"
				data-min-width={splitterMainSecondPaneMinWidthPx}
			>
				{isNoActiveView ? (
					<EmptyListViewV5
						onAddClick={this.onAddClick}
						feature={XyiconFeature.View}
					/>
				) : (
					<>
						{!isInMixedOrSplitViewMode && nonDockedTitle && (
							<DockableV5
								spaceViewRenderer={spaceViewRenderer}
								setDocked={this.props.setDockableDocked}
								setOpen={this.props.setDockableOpen}
								isDocked={false}
								title={nonDockedTitle}
								setActiveTool={this.props.setActiveTool}
								onAddCatalogClick={this.props.onAddCatalogClick}
								onDuplicateCatalogClick={this.props.onDuplicateCatalogClick}
								onCreateUnplottedXyicons={Functions.emptyFunction}
							/>
						)}
						<CanvasContainerStyled
							ref={this._canvasContainer}
							style={canvasContainerBorderStyle}
						>
							{!isInMixedOrSplitViewMode && (
								<>
									<SpaceToolBarV5
										ref={this._spaceToolBarRef}
										spaceViewRenderer={spaceViewRenderer}
										mode={this.props.mode}
										activeToolBar={this.state.activeToolBar}
										setActiveToolBar={this.setActiveToolBar}
										setDockableOpen={this.props.setDockableOpen}
										isCatalogOpen={this.props.isCatalogOpen}
										isBoundaryTypesWindowOpen={this.props.isBoundaryTypesWindowOpen}
										isUnplottedXyiconsOpen={this.props.isUnplottedXyiconsOpen}
										activeToolId={this.props.activeToolId}
										setActiveTool={this.props.setActiveTool}
										selectionToolType={this.props.selectionToolType}
									/>
									{this.spaceViewRenderer.isMeasureToolBarOpen && (
										<MeasureToolBarV5
											spaceViewRenderer={this.spaceViewRenderer}
											activeToolId={this.props.activeToolId}
											setActiveTool={this.props.setActiveTool}
										/>
									)}
									{this.props.mode === SpaceEditorMode.NORMAL && this.state.activeToolBar !== SpaceToolBarType.SpaceAlignToolBar && (
										<>
											<SpaceViewBarV5
												onOpenPanelClick={this.props.onOpenPanelClick}
												activePanel={this.props.activePanel}
												divRef={this._spaceViewBarRef}
												dropdownStyle={this.state.dropdownStyle}
											/>
											<div className="sceneOptions hidden">
												<XyiconSizeChanger spaceViewRenderer={this.spaceViewRenderer} />
											</div>
										</>
									)}
									{(this.props.mode === SpaceEditorMode.ALIGN || this.state.activeToolBar === SpaceToolBarType.SpaceAlignToolBar) && (
										<SpaceAlignViewBarV5 spaceViewRenderer={this.spaceViewRenderer} />
									)}
									{actionBar.isOpen && linkBreakers.fromObjectIds.length === 0 && !KeyboardListener.isCtrlDown && (
										<SpaceActionBarV5
											ref={this._spaceActionBarRef}
											spaceViewRenderer={this.spaceViewRenderer}
											isEditEnabled={actionBar.isEditable}
											isInEditMode={actionBar.isInEditMode}
											isLinkEnabled={actionBar.isLinkable}
											getWorldPosition={this.getActionBarWorldPosition}
											onRedrawClick={this.onRedrawClick}
											onCancelEditClick={this.onCancelEditClick}
											onApplyEditClick={this.onApplyEditClick}
											onDeleteClick={this.onDeleteClick}
											openMergeBoundariesWindow={this.openMergeBoundariesWindow}
											toggleDetailsPanel={this.props.toggleDetailsPanel}
											isDetailsPanelOpen={this.props.isDetailsPanelOpen}
										/>
									)}
									{mergeBoundariesWindow.isOpen && <MergeBoundariesWindowV5 onClose={this.closeMergeBoundariesWindow} />}
									{contextMenu.isOpen && (
										<SpaceContextMenu
											spaceViewRenderer={spaceViewRenderer}
											getWorldPosition={this.getContextMenuWorldPosition}
											positionUpdateSignal={this._floatingOverlaySignals.contextMenuUpdated}
											onCopyClick={spaceViewRenderer.copySelectedItemsToClipboard}
											onCopyToStampClick={spaceViewRenderer.copySelectedItemsToStamp}
											onCutClick={spaceViewRenderer.cutSelectedItemsToClipboard}
											onDeleteClick={this.onDeleteClick}
											onPasteClick={this.onPasteClick}
											onRealignSpaceClick={this.onRealignSpaceClick}
											onClose={this.closeContextMenu}
										/>
									)}
									{portSelector.isOpen && (
										<PortSelectorV5
											spaceViewRenderer={spaceViewRenderer}
											getWorldPosition={this.getPortSelectorWorldPosition}
											positionUpdateSignal={this._floatingOverlaySignals.portSelectorUpdated}
											item={portSelector.item}
											ports={portSelector.ports}
											type={portSelector.type}
											divRef={this._portSelectorRef}
										/>
									)}
									{linkBreakers.fromObjectIds.length > 0 && (
										<LinkLineActionBarV5
											spaceViewRenderer={spaceViewRenderer}
											fromObjectIds={linkBreakers.fromObjectIds}
											toggleDetailsPanel={this.props.toggleDetailsPanel}
											isDetailsPanelOpen={this.props.isDetailsPanelOpen}
											forceUpdateDetailsPanel={this.props.forceUpdateDetailsPanel}
										/>
									)}
									{linkedXyicons.fromObjectIds.length === 1 && ( // TODO: what should happen if there are more than 1 xyicons with off-space links? As a temporary solution we don't show any popups now
										<LinkedXyiconsWindowV5
											spaceViewRenderer={spaceViewRenderer}
											objectAID={linkedXyicons.fromObjectIds[0]}
											type={linkedXyicons.type}
											onClose={this.closeLinkedXyiconsWindow}
										/>
									)}
									{this.props.mode === SpaceEditorMode.NORMAL &&
										this.state.activeToolBar !== SpaceToolBarType.SpaceAlignToolBar &&
										this.props.appState.space && (
											<SpaceSelector
												spaces={this.props.appState.actions.getList<Space>(XyiconFeature.Space)}
												selectedSpace={this.props.appState.space}
												onSpaceSelect={(space: Space) =>
													this.props.appState.app.navigation.goApp({
														mainNav: NavigationEnum.SPACE,
														subNavs: [space.refId],
													})
												}
												divRef={this._spaceSelectorRef}
											/>
										)}
									{this.spaceViewRenderer.toolManager && spaceViewRenderer.toolManager.cameraControls && this.props.thumbnail && (
										<SpaceControlBarV5
											spaceViewRenderer={spaceViewRenderer}
											onRealignSpaceClick={this.onRealignSpaceClick}
											navigationBoxImgSrc={this.props.thumbnail}
										/>
									)}
									{hasChanges && changedTypes && (
										<AlertV5
											divRef={this._saveViewBarRef}
											feature={XyiconFeature.SpaceEditor}
											spaceViewRenderer={this.spaceViewRenderer}
											type={changedTypes}
											isDetailsPanelOpen={this.props.isDetailsPanelOpen}
											dropdownStyle={this.state.saveViewDropdownStyle}
											isSideGrid={this.props.isSideGridOpen}
											onClose={this.closeSaveViewRelatedPopups}
										/>
									)}
									{this.spaceViewRenderer.spaceItemController.lockIconManager.hoveredLockInstanceId > -1 && (
										<LockIconTooltip spaceViewRenderer={this.spaceViewRenderer} />
									)}
								</>
							)}
						</CanvasContainerStyled>
					</>
				)}
				<SpaceLoadingScreenV5 spaceViewRenderer={spaceViewRenderer} />
			</SpaceEditorStyled>
		);
	}
}

const SpaceEditorStyled = styled.div`
	position: relative;
	width: 100%;
	overflow: hidden;
	display: flex;
	min-height: 0;
	height: 100%;

	canvas {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}
`;

const CanvasContainerStyled = styled.div`
	position: relative;
	display: flex;
	flex: 1;
	min-height: 0;
`;
